<template>
  <view v-if="status === 'success'" class="auth-content text-gray text-14 bg-light">
    <slot />
  </view>
  <view v-else class="auth-mask">
    <wd-loading v-if="status === 'loading'" />
    <StatusTips v-else-if="status === 'no-login'" code="no-login" @retry="goLogin" />
    <StatusTips v-else-if="status === 'forbidden'" code="forbidden" @retry="returnBack" />
  </view>
</template>

<script setup lang="ts">
import { goLoginPage } from '@/router/back';
import { LOGIN_PAGE } from '@/router/config';
import { useTokenStore, useUserStore } from '@/store';

const props = defineProps<{
  needLogin?: boolean
  requiredRole?: string
}>();

const tokenStore = useTokenStore();
const userStore = useUserStore();
const status = ref<'loading' | 'success' | 'no-login' | 'forbidden'>('success');

const checkAuth = () => {
  // 退出登录进行中，跳过检查，避免 redirectTo 与 switchTab 竞态
  if (tokenStore.isLoggingOut) {
    return;
  }

  // 1. 当前需要登录获取需要权限使用，检测是否已登录
  if ((props.needLogin || props.requiredRole) && !tokenStore.updateNowTime().hasLogin) {
    // 自动跳转到登录页，携带当前页面路径作为 redirect 参数
    const pages = getCurrentPages();
    const currPage = pages[pages.length - 1] as any;
    let fullPath = currPage ? `/${currPage.route}` : '/';
    if (currPage?.options && Object.keys(currPage.options).length > 0) {
      const queryStr = Object.entries(currPage.options)
        .map(([k, v]) => `${k}=${v}`)
        .join('&');
      fullPath += `?${queryStr}`;
    }
    // 避免在登录页本身循环跳转
    if (fullPath.startsWith(LOGIN_PAGE)) {
      status.value = 'no-login';
      return;
    }
    const redirectUrl = `${LOGIN_PAGE}?redirect=${encodeURIComponent(fullPath)}`;
    uni.redirectTo({ url: redirectUrl });
    return;
  }

  // 2. 当前需要角色检测
  if (props.requiredRole && !userStore.perms.includes(props.requiredRole)) {
    // 需要权限
    status.value = 'forbidden';
    return;
  }

  // 4. 登录及权限都通过
  status.value = 'success';
};

const goLogin = () => {
  goLoginPage();
};

const returnBack = () => {
  uni.navigateBack();
};

watch(() => tokenStore.tokenInfo, checkAuth);
watch(() => userStore.perms, checkAuth);

onMounted(() => {
  checkAuth();
});
</script>

<style scoped lang="scss">
.auth-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 750rpx;
  height: 100%;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;

  /* 适配全面屏/分屏的安全区域 */
  //padding-top: env(safe-area-inset-top);
  //padding-bottom: env(safe-area-inset-bottom);
}

.auth-mask {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>
